import { useMutation } from "@apollo/client";
import { Button, DialogActions, DialogContentText } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ToolTip from "@material-ui/core/Tooltip";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";
import _ from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { PatientInformation, Test } from "../../graphql/baseTypes";
import { RESEND_TEST_EMAIL_MULTIPLE } from "../../graphql/mutations";
import { ResendTestAdminMultipleMutation } from "../../graphql/mutations/resendTestAdminMultiple";
import { ResendTestAdminMultipleMutationVariables } from "../../graphql/types";
import { generateIdFromTest } from "../../utils/tests";
interface DisplayTest {
  date: string;
  testId: string;
  patient: PatientInformation;
  sent?: boolean;
  hasEmail?: boolean;
}

interface Props {
  ids?: Array<Test>;
  open: boolean;
  handleClose: () => any;
}

const ResendDialog: React.FC<Props> = ({ open, handleClose, ids }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [resend, { loading: resendLoading, data, called }] = useMutation<
    ResendTestAdminMultipleMutation,
    ResendTestAdminMultipleMutationVariables
  >(RESEND_TEST_EMAIL_MULTIPLE, {
    onError(error) {
      console.log(error);
      enqueueSnackbar("Emails konnte nicht versendet werden!", {
        variant: "error",
      });
    },
    onCompleted() {
      enqueueSnackbar("Emails wurde versendet", {
        variant: "success",
      });
    },
  });
  const { resendTestAdminMultiple } = data || {};

  const handleSubmit = () => {
    if (!ids) {
      return;
    }
    return resend({
      variables: {
        input: ids.map(({ date, locationId, testId }) => ({
          date,
          locationId,
          testId,
        })),
      },
    });
  };

  const items = React.useMemo<Array<DisplayTest>>(() => {
    if (!ids) {
      return [];
    }
    const generated: Array<DisplayTest> = ids.map(
      ({ date, testId, patient }) => ({
        date,
        testId,
        patient,
        hasEmail: !!patient.email,
      })
    );
    if (!resendTestAdminMultiple) {
      return generated;
    } else {
      const other = _.values(
        _.merge(
          _.keyBy(generated, (test) => generateIdFromTest(test)),
          _.keyBy(resendTestAdminMultiple, (sendInformation) =>
            generateIdFromTest(sendInformation)
          )
        )
      );
      return other;
    }
  }, [ids, resendTestAdminMultiple]);

  const selectDisplayIcon = (
    sent: boolean = false,
    hasEmail: boolean = false
  ) => {
    if (resendLoading) {
      if (!hasEmail) {
        return (
          <ToolTip title="Keine Email">
            <WarningIcon />
          </ToolTip>
        );
      } else {
        return <CircularProgress size={30} />;
      }
    } else {
      if (called) {
        if (!hasEmail) {
          return (
            <ToolTip title="Keine Email">
              <WarningIcon />
            </ToolTip>
          );
        } else {
          if (sent) {
            return (
              <ToolTip title="Erfolgreich gesendet">
                <CheckCircleIcon color="primary" />
              </ToolTip>
            );
          } else {
            return (
              <ToolTip title="Fehler beim senden">
                <ErrorIcon color="error" />
              </ToolTip>
            );
          }
        }
      } else {
        if (!hasEmail) {
          return (
            <ToolTip title="Keine Email">
              <WarningIcon />
            </ToolTip>
          );
        } else {
          return (
            <ToolTip title="Noch nicht versendet">
              <InfoIcon />
            </ToolTip>
          );
        }
      }
    }
  };

  return (
    <Dialog
      open={open}
      disableBackdropClick={resendLoading}
      disableEscapeKeyDown={resendLoading}
      onClose={resendLoading ? () => {} : handleClose}
      fullWidth
      scroll={"paper"}
      aria-labelledby="test-resend-mulipile-dialog-title"
    >
      <DialogTitle id="test-resend-mulipile-title">
        Testberichte erneut versenden
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Hier sehen Sie ein Übersicht über die Ausgewählten Tests, den sende
          Status sowie ob eine Email verfügbar ist.
        </DialogContentText>
        <List>
          {items.map(({ testId, date, patient, sent, hasEmail }) => (
            <ListItem>
              <ListItemText
                primary={`Test ${testId} am ${moment(date).format(
                  "ll"
                )} um ${moment(date).format("LT")} Uhr`}
                secondary={`${patient.firstname} ${patient.lastname}`}
              />
              <ListItemIcon>{selectDisplayIcon(sent, hasEmail)}</ListItemIcon>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        {!called && (
          <Button
            onClick={handleClose}
            disabled={resendLoading}
            color="primary"
            variant="outlined"
          >
            Abbrechen
          </Button>
        )}
        <Button
          onClick={called ? handleClose : handleSubmit}
          disabled={resendLoading}
          color="primary"
        >
          {called ? "Ok" : "Versenden"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResendDialog;
